import React from 'react';
import {Paper, Typography, Box, Chip, LinearProgress, Tooltip, IconButton} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { getAchievementColor, formatNumber, formatPercent, getTransmitCycleBadgeColorByLabel } from '../utils';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const fontSizeSet = {
    xSmall : '0.95rem',
    small : '1.1rem',
    medium : '1.2rem',
    large : '1.3rem',
}

const styles = {
    // 최상단 카드 스타일
    paper: {
        p: { xs: 1.2, sm: 1.3, md: 1.4, lg: 1.5 },
        minHeight: {
            xs: '140px',    // 최소 높이만 지정
            sm: '150px',
            md: '160px',
            lg: '170px',
            xl: '180px'
        },
        height: 'auto',     // 내용에 따라 자동 조정
        minWidth: 0,
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
        },
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
    },

    // 회사 정보 헤더
    companyInfoHeader: {
        outerBox: {
            display: 'flex',
            alignItems: 'center',
            mb: 1.5,
            justifyContent: 'center',
        },

        innerBox: {
            backgroundColor: '#4c4c4c',
            borderRadius: '6px',
            px: 1.5,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            maxWidth: '100%',
        },

        companyBusinessIcon: {
            mr: 0.5,
            color: '#ffffff',
            // fontSize: 14,
            fontSize: fontSizeSet.small,
        },

        companyTypography: {
            color: '#ffffff',
            fontSize: fontSizeSet.small,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            letterSpacing: '-0.5px',
        }
    },

    kpiIndicatorInfo: {
        outbox: {
            mb: 1.5
        },

        middleBox: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.5,
        },

        innerBox: {
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flex: 1,
            minWidth: 0,
        },

        kpiIndicatorName:{
            color: '#333',
            fontSize: fontSizeSet.xSmall,
            lineHeight: '1.2',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            overflow: 'visible',
        },

        transmitCycleLabel: {
            fontSize: fontSizeSet.xSmall,
            height: '20px',
            color: 'white',
            fontWeight: 'bold',
            '& .MuiChip-icon': {
                color: 'white',
                fontSize: '10px'
            },
        },

        achievementRateTypography: {
            fontSize: fontSizeSet.xSmall,
            ml: 0.5
        },

        goalRatio: {
            color: '#666',
            fontSize: fontSizeSet.xSmall,
            fontWeight: 500
        },

    },
}

// companyPopupOpen,
const CompanySummaryCard = ({company, availableKPIs, onDetailClick}) => {
    if (!company || !company.subscriptions) {
        return (
            <Paper elevation={1} sx={{ p: 2, height: '200px', backgroundColor: '#f5f5f5' }}>
                <Box sx={{ textAlign: 'center', color: '#999', mt: 4 }}>
                    <Typography variant="body2">데이터 없음</Typography>
                </Box>
            </Paper>
        );
    }

    const activeSubscriptions = company.subscriptions.filter(sub =>
        sub.kpiData && sub.kpiData.length > 0
    );

    return (
        <Paper
            elevation={2}
            sx={ styles.paper }
        >
            {/* 회사 정보 헤더 */}
            <Box sx={ styles.companyInfoHeader.outerBox }>
                <Box sx={ styles.companyInfoHeader.innerBox }>
                    <BusinessIcon sx={ styles.companyInfoHeader.companyBusinessIcon } />
                    <Typography
                        variant="subtitle1"
                        fontWeight="700"
                        sx = { styles.companyInfoHeader.companyTypography
                        }>
                        {company.companyName}
                    </Typography>
                </Box>
            </Box>

            {/* KPI 구독 정보 (최대 2개 표시) */}
            <Box sx={{ flex: 1 }}>
                {activeSubscriptions.slice(0, 2).map((subscription, index) => {
                    const latestKpiData = subscription.kpiData[subscription.kpiData.length - 1];
                    const measuredValue = latestKpiData.measuredValue || 0;

                    return (
                        <Box key={subscription.kpiIndicatorCode} sx={ styles.kpiIndicatorInfo.outbox }>
                            {/* KPI 이름과 전송주기 배지, 달성률 */}
                            <Box sx={ styles.kpiIndicatorInfo.middleBox } >
                                <Box sx={ styles.kpiIndicatorInfo.innerBox }>
                                    <Typography
                                        variant="body2"
                                        fontWeight="600"
                                        sx={ styles.kpiIndicatorInfo.kpiIndicatorName }
                                    >
                                        {subscription.kpiIndicatorName}
                                    </Typography>
                                    <Chip
                                        icon={<ScheduleIcon sx={{ fontSize: 10 }} />}
                                        label={subscription.transmitCycleLabel}
                                        size="small"
                                        sx={{
                                            ...styles.kpiIndicatorInfo.transmitCycleLabel,
                                            backgroundColor: getTransmitCycleBadgeColorByLabel(subscription.transmitCycleLabel),
                                        }}
                                    />
                                </Box>
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{
                                        ...styles.kpiIndicatorInfo.achievementRateTypography,
                                        color: getAchievementColor((measuredValue / subscription.targetValue) * 100)
                                    }}
                                >
                                    {formatNumber(measuredValue, 2)} {latestKpiData.unit || ''}
                                </Typography>
                            </Box>

                            {/* 진행률 바 */}
                            <LinearProgress
                                variant="determinate"
                                value={Math.min((measuredValue / subscription.targetValue) * 100, 100)}
                                sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: '#f0f0f0',
                                    mb: 0.5,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getAchievementColor((measuredValue / subscription.targetValue) * 100),
                                        borderRadius: 2
                                    }
                                }}
                            />

                            {/* 현재값/목표값 */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography
                                    variant="caption"
                                    sx={styles.kpiIndicatorInfo.goalRatio}
                                >
                                    {formatNumber(Math.floor((latestKpiData.measuredValue || 0) * 100) / 100, 2)} / {formatNumber(Math.floor((subscription.targetValue || 0) * 100) / 100, 2)} {latestKpiData.unit || ''}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#999',
                                        fontSize: '0.55rem',
                                        fontStyle: 'italic',
                                        mt: 0.2
                                    }}
                                >
                                    (측정값 / 목표값)
                                </Typography>
                            </Box>
                            {/*<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 2 }} />*/}
                        </Box>
                    );
                })}

                {/* 더 많은 KPI가 있을 때 표시 */}
                {activeSubscriptions.length > 2 && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#666',
                            fontSize: '0.6rem',
                            textAlign: 'center',
                            display: 'block',
                            mt: 0.5
                        }}
                    >
                        외 {activeSubscriptions.length - 2}개 KPI 구독 중
                    </Typography>
                )}
            </Box>

            {/* 상세 보기 버튼 */}
            <Box sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1
            }}>
                <Tooltip title="상세 그래프 보기">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDetailClick?.(company.companyCode, company.companyName);
                        }}
                        sx={{
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            color: '#1976d2',
                            width: 28,
                            height: 28,
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    );
};

export default CompanySummaryCard;